<template>
    <AuthLayout>
        <loader v-if="loading" />
        <div v-else>
            <p class="login-subtitle" v-lang="'login-signupTitle'"></p>
            
            <form @submit.prevent="submit" ref="form" class="login-form">
                <div class="input-group" :class="{ 'has-error': fieldErrors.first_name }">
                    <label>
                        <span v-lang="'login-firstNameLabel'"></span>
                        <span class="required-asterisk">*</span>
                    </label>
                    <div class="input-wrapper">
                        <input type="text" v-model="first_name" required :disabled="saving" :placeholder="t('login-firstNamePlaceholder')" />
                    </div>
                    <span class="error-message" v-if="fieldErrors.first_name">{{ fieldErrors.first_name }}</span>
                </div>
                
                <div class="input-group" :class="{ 'has-error': fieldErrors.last_name }">
                    <label>
                        <span v-lang="'login-lastNameLabel'"></span>
                        <span class="required-asterisk">*</span>
                    </label>
                    <div class="input-wrapper">
                        <input type="text" v-model="last_name" required :disabled="saving" :placeholder="t('login-lastNamePlaceholder')" />
                    </div>
                    <span class="error-message" v-if="fieldErrors.last_name">{{ fieldErrors.last_name }}</span>
                </div>
                
                <div class="input-group" :class="{ 'has-error': fieldErrors.birthdate }">
                    <label>
                        <span v-lang="'login-birthdateLabel'"></span>
                        <span class="required-asterisk">*</span>
                    </label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                        </span>
                        <input type="tel" v-model="birthdate" v-mask="'##/##/####'" @input="validateBirthdate()" required :disabled="saving" :placeholder="t('login-birthdatePlaceholder')" />
                    </div>
                    <span class="error-message" v-if="fieldErrors.birthdate">{{ fieldErrors.birthdate }}</span>
                </div>
                
                <div class="input-group" :class="{ 'has-error': fieldErrors.email }">
                    <label>
                        <span v-lang="'login-emailLabel'"></span>
                        <span v-if="clientEmailRequired" class="required-asterisk">*</span>
                    </label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                        </span>
                        <input type="email" v-model="email" @input="validateEmail()" :required="clientEmailRequired" :disabled="saving" :placeholder="t('login-emailPlaceholder')" />
                    </div>
                    <span class="error-message" v-if="fieldErrors.email">{{ fieldErrors.email }}</span>
                </div>
                
                <div class="action-buttons-row">
                    <button type="button" class="btn-icon" @click="logout" :disabled="saving">
                        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                    </button>
                    
                    <button type="submit" class="btn-primary" :disabled="saving" :class="{ 'is-loading': saving }">
                        <span v-lang="'login-signUpButton'"></span>
                    </button>
                </div>
            </form>
        </div>
    </AuthLayout>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import loader from "../../shared/loader";
import Api from "../../api";
import config from "../../config";
import AuthLayout from "./auth-layout.vue";

export default {
    components: {
        loader,
        AuthLayout
    },
    data: () => ({
        loading: false,
        saving: false,
        first_name: "",
        last_name: "",
        email: "",
        birthdate: "",
        fieldErrors: {}
    }),
    mounted() {
        this.loading = true;
        this.$store
            .dispatch("updateClientInfo")
            .then((client) => {
                this.first_name = client.first_name;
                this.last_name = client.last_name;
                this.email = client.email;
                this.birthdate = client.birthdate;
            })
            .catch(() => {})
            .then(() => {
                this.loading = false;
            });
    },
    methods: {
        validateEmail() {
            const emailPattern = /^[^\s@]+(\.[^\s@]+)*@[^\s@]+\.[^\s@]+$/;
            const invalidPattern = /(^\.|\.$)/;
            if (!this.email) {
                this.fieldErrors.email = this.clientEmailRequired
                    ? "O email é obrigatório."
                    : "";
            } else if (invalidPattern.test(this.email.split("@")[0])) {
                this.fieldErrors.email = "O email deve ser válido.";
            } else if (!emailPattern.test(this.email)) {
                this.fieldErrors.email = "O email deve ser válido.";
            } else {
                this.fieldErrors.email = "";
            }
        },
        submit() {
            this.fieldErrors = {};

            if (this.saving || !this.$refs.form.reportValidity()) {
                return;
            }

            if (config.isPreview) {
                this.$store.commit("setClient", {
                    ...this.$store.state.client,
                    first_name: this.first_name,
                    last_name: this.last_name,
                    email: this.email,
                    birthdate: this.birthdate,
                });
                this.$router.push("/dashboard").catch(() => {});

                return;
            }

            this.saving = true;
            Api.post("client/profile", {
                first_name: this.first_name,
                last_name: this.last_name,
                email: this.email,
                birthdate: this.birthdate,
            })
            .then((response) => {
                this.$store.commit("setClient", response.body.client);
                this.$router.push("/dashboard");
            })
            .catch((err) => {
                if(err.body && err.body.errors){
                    this.fieldErrors = err.body.errors;
                }
            })
            .then(() => {
                this.saving = false;
            });
        },
        logout() {
            this.$store.dispatch("logout");
        },
        validateBirthdate() {
            if (!this.birthdate) {
                this.fieldErrors.birthdate = "Data de nascimento inválida!";
                return;
            }
            const parts = this.birthdate.split("/");
            if (parts.length !== 3) {
                this.fieldErrors.birthdate = "Data de nascimento inválida!";
                return;
            }
            const [day, month, year] = parts.map(Number);
            const date = new Date(year, month - 1, day);
            if (
                date.getFullYear() === year &&
                date.getMonth() === month - 1 &&
                date.getDate() === day
            ) {
                this.fieldErrors.birthdate = "";
            } else {
                this.fieldErrors.birthdate = "Data de nascimento inválida!";
            }
        },
    },
    computed: {
        ...mapState({
            client: (state) => state.client,
        }),
        ...mapGetters(["clientEmailRequired"]),
    },
};
</script>

